/**
 * BooleanResponse — Yes/No button pair. Immediate-submit by default
 * (tapping Yes fires onSubmit(true) without a separate commit step);
 * set `stage: true` to require a second click on a submit button.
 *
 * Symbol + text on every state per the colorblind constraint.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { ResponseCoreProps } from './types';
import './responses.css';

interface Props extends ResponseCoreProps<boolean> {
  /** Override the Yes/No button labels. Defaults to "Yes" / "No". */
  yesLabel?: string;
  noLabel?: string;
  /** If true, Yes/No sets a draft state; user must then click submit.
   *  Default false — buttons commit immediately. */
  stage?: boolean;
}

export function BooleanResponse({
  label,
  hideLabel,
  initialValue = null,
  onChange,
  onSubmit,
  disabled,
  autoFocus,
  hideSubmit,
  submitLabel = 'Send',
  helper,
  yesLabel = 'Yes',
  noLabel = 'No',
  stage = false,
}: Props) {
  const [value, setValue] = useState<boolean | null>(initialValue ?? null);
  const yesRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (autoFocus) yesRef.current?.focus();
  }, [autoFocus]);

  function pick(v: boolean) {
    setValue(v);
    onChange?.(v);
    if (!stage) onSubmit?.(v);
  }

  function commit(e?: FormEvent) {
    if (e) e.preventDefault();
    if (value === null) return;
    onSubmit?.(value);
  }

  return (
    <form className="cf-response cf-boolean-response" onSubmit={commit}>
      <div
        className="cf-boolean-response__legend"
        role="group"
        aria-label={label}
      >
        {!hideLabel && <div className="cf-response__label">{label}</div>}
        <div className="cf-boolean-response__buttons">
          <button
            ref={yesRef}
            type="button"
            className={
              value === true
                ? 'cf-btn cf-btn--primary'
                : 'cf-btn cf-btn--ghost'
            }
            onClick={() => pick(true)}
            disabled={disabled}
            aria-pressed={value === true}
          >
            <span aria-hidden="true">✓</span>
            <span>{yesLabel}</span>
          </button>
          <button
            type="button"
            className={
              value === false
                ? 'cf-btn cf-btn--primary'
                : 'cf-btn cf-btn--ghost'
            }
            onClick={() => pick(false)}
            disabled={disabled}
            aria-pressed={value === false}
          >
            <span aria-hidden="true">✗</span>
            <span>{noLabel}</span>
          </button>
        </div>
      </div>
      {helper && <div className="cf-response__helper">{helper}</div>}
      {stage && !hideSubmit && (
        <div className="cf-response__actions">
          <button
            type="submit"
            className="cf-btn cf-btn--primary"
            disabled={disabled || value === null}
          >
            {submitLabel}
          </button>
        </div>
      )}
    </form>
  );
}
